import { ProgramCreateParams, ProgramType } from '@citruscamps/citrus-client'
import { useFormik } from 'formik'
import React, { useEffect } from 'react'
import CreatableSelect from 'react-select/creatable'
import * as Yup from 'yup'
import { Checkbox, CheckboxProps } from '../../../../../components/Checkbox/Checkbox'
import { LoadingButton } from '../../../../../components/LoadingButton/LoadingButton'
import { Title } from '../../../../../components/Title/Title'
import translations from '../../../../../translations/en.json'
import { getSelectClasses } from '../../../../../utils/branding-utils'
import { getSuggestedByTypes } from '../../../../../utils/program-types'

interface IProps {
  initialValues?: ProgramCreateParams
  onPrevious?: (value: ProgramCreateParams) => Promise<void> | void
  onSubmit: (value: ProgramCreateParams) => Promise<void>
}

export const MoreDetailStep = ({
  initialValues,
  onPrevious,
  onSubmit,
}: IProps): React.ReactElement => {
  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleBlur,
    setValues,
    setFieldError,
    setFieldValue,
    isSubmitting,
  } = useFormik<ProgramCreateParams>({
    initialValues: {
      ...(initialValues || {}),
      traffic_source: initialValues?.traffic_source
        ? {
            utm_source: initialValues?.traffic_source?.utm_source || 'direct',
            utm_medium: initialValues?.traffic_source?.utm_medium || 'none',
            utm_campaign: initialValues?.traffic_source?.utm_campaign || 'none',
            utm_term: initialValues?.traffic_source?.utm_term,
            utm_content: initialValues?.traffic_source?.utm_content,
          }
        : undefined,
      member_title: initialValues?.member_title || '',
      name: initialValues?.name || '',
      address: initialValues?.address!,
      types: initialValues?.types || [],
      tags: initialValues?.tags || [],
      language: initialValues?.language || navigator.language,
      timezone: initialValues?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    validationSchema: Yup.object().shape({
      member_title: Yup.string().max(45, (translations as any)['error.title_maxlength']),
      name: Yup.string()
        .required((translations as any)['error.program_name_required'])
        .max(40, (translations as any)['error.program_name_maxlength']),
      types: Yup.array()
        .of(Yup.string())
        .min(1, (translations as any)['error.program_type_required']),
      tags: Yup.array().of(Yup.string()),
    }),
    onSubmit: async (value, { setSubmitting }) => {
      try {
        await onSubmit(value)
      } catch (e: any) {
        if (e.param) setFieldError(e.param, e.message)
      }
      setSubmitting(false)
    },
  })

  useEffect(() => {
    setValues({
      ...(initialValues || {}),
      member_title: initialValues?.member_title || '',
      name: initialValues?.name || '',
      address: initialValues?.address!,
      types: initialValues?.types || [],
      tags: initialValues?.tags || [],
      language: initialValues?.language || navigator.language,
      timezone: initialValues?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
  }, [initialValues, setValues])

  const handleCheckbox = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, ...target } = event.target
    const checked = typeof target.checked !== 'undefined' ? !!target.checked : !!value
    const field: any[] = (values as any)?.[name] || []
    setFieldValue(
      name,
      checked
        ? field?.includes(value)
          ? field
          : field.concat([value])
        : field.filter((s) => s !== value),
      true,
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <Title value="Setup Program" />
      <h4 className="mb-4">Let us know how we can help to assist you.</h4>
      <div className="mb-3">
        <label className="mb-0">Your type of program</label>
        <div className="small text-body-secondary">You can select more than one.</div>
        {[
          { label: 'Sport Camp', value: 'sports', onChange: handleCheckbox },
          { label: 'Education Camp', value: 'education', onChange: handleCheckbox },
          { label: 'Day Camp', value: 'day', onChange: handleCheckbox },
          { label: 'Traditional Camp', value: 'traditional', onChange: handleCheckbox },
          { label: 'Other', value: 'other', onChange: handleCheckbox },
        ].map((props: CheckboxProps) => (
          <div key={props.value}>
            <Checkbox
              {...props}
              name="types"
              className={'py-1' + (touched.types && !!errors.types ? ' is-invalid' : '')}
              checked={values.types.includes(props.value as ProgramType)}
              onBlur={handleBlur}
            />
          </div>
        ))}
        <div className="invalid-feedback d-block">
          {touched.types && !!errors.types ? errors.types : '\u00A0'}
        </div>
      </div>
      <div className="mb-3">
        <label className="optional mb-0">Your program focus</label>
        <div className="small text-body-secondary">
          These tags help your customers find your events.
        </div>
        <CreatableSelect
          inputId="tags"
          isMulti
          placeholder="Add tags..."
          onChange={(val: any) =>
            setFieldValue(
              'tags',
              (val || []).map((v: any) => v.value),
              true,
            )
          }
          options={getSuggestedByTypes(values.types || [])}
          formatOptionLabel={(option) => option.label}
          formatCreateLabel={(val) => `${val} (New tag)`}
          value={(values.tags || []).map((t) => ({ value: t, label: t }))}
          isClearable
          noOptionsMessage={() => 'No suggestions'}
          classNames={getSelectClasses()}
          unstyled
        />
        <div className="invalid-feedback d-block">
          {touched.tags && !!errors.tags ? errors.tags : '\u00A0'}
        </div>
      </div>
      <div className="d-flex justify-content-between mt-4">
        {onPrevious && (
          <button
            type="button"
            className="btn btn-link me-auto"
            disabled={isSubmitting}
            onClick={() => onPrevious(values)}
          >
            Back
          </button>
        )}
        <LoadingButton type="submit" className="btn btn-primary ms-auto" loading={isSubmitting}>
          Next
        </LoadingButton>
      </div>
    </form>
  )
}
